import * as Y from "yjs";
import { widgetsMap } from "@playbookai/collab-client";
import { stickyNotesMap } from "@playbookai/ui";
import { applyPhaseAreaResize, phaseAreasMap } from "./phase-area-doc-shape.js";
import type { PhaseAreaResizePlan } from "./phase-areas.js";
import {
  widgetPlacementsApi,
  type ApiClientConfig,
  type PlacementResponse,
} from "../api/playbook-be-client.js";

const saving = new WeakSet<Y.Doc>();
export const isPhaseAreaResizeSaving = (doc: Y.Doc): boolean => saving.has(doc);

/** Roll back this resize's Yjs changes if its atomic placement batch fails. */
export async function savePhaseAreaResize(
  doc: Y.Doc,
  plan: PhaseAreaResizePlan | null,
  solveId: string,
  config: ApiClientConfig,
  applyAdditional?: () => void,
): Promise<PlacementResponse[]> {
  if (saving.has(doc))
    throw new Error(
      "A phase area is still being saved. Please try again when it finishes.",
    );
  saving.add(doc);
  const origin = {};
  const undo = new Y.UndoManager(
    [phaseAreasMap(doc), widgetsMap(doc), stickyNotesMap(doc)],
    {
      trackedOrigins: new Set([origin]),
      captureTimeout: 0,
    },
  );
  try {
    let moved: readonly string[] = [];
    doc.transact(() => {
      if (plan) moved = applyPhaseAreaResize(doc, plan).movedWidgetIds;
      applyAdditional?.();
    }, origin);
    const placements = moved.flatMap((id) => {
      const map = widgetsMap(doc).get(id);
      const widgetId = map?.get("widgetId");
      if (!map || typeof widgetId !== "string") return [];
      const phase = map.get("phase");
      const position: Record<string, unknown> = {};
      for (const [key, fallback] of [
        ["x", 0],
        ["y", 0],
        ["w", 220],
        ["h", 150],
      ] as const) {
        const value = map.get(key);
        position[key] =
          typeof value === "number" && Number.isFinite(value)
            ? value
            : fallback;
      }
      if (
        map.get("isDefaultRoster") === true &&
        typeof map.get("rosterKey") === "string"
      ) {
        position.isDefaultRoster = true;
        position.rosterKey = map.get("rosterKey");
      }
      return [
        {
          widgetId,
          position,
          ...(typeof phase === "string"
            ? { phase: phase === "any" ? null : phase }
            : {}),
        },
      ];
    });
    return placements.length
      ? await widgetPlacementsApi.moveBatch(config, {
          surface: "solve_canvas",
          surfaceId: solveId,
          placements,
        })
      : [];
  } catch (error) {
    // Yjs undo removes this origin only; later peer edits remain intact.
    undo.undo();
    throw error;
  } finally {
    undo.destroy();
    saving.delete(doc);
  }
}
